import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { Project, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useUserDirectory } from "@/modules/tasks/directories";
import { describeRejection } from "@/modules/tasks/writes";

/** Matches `routes/tasks.ts`'s own `key` pattern, checked client-side so a bad key never reaches the API. */
const KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;

/**
 * A first guess at a key from a project's name — never the final answer,
 * since a key is immutable the moment it exists (#399, spec §8) and the
 * person naming the project is the one who should confirm it.
 *
 * One letter per word, uppercased; a one-word name instead takes that word's
 * first four letters, because a single-letter key would fail
 * `KEY_PATTERN`'s two-character minimum before anyone got to edit it.
 */
function suggestKey(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/[^a-zA-Z0-9]/g, ""))
    .filter((word) => word !== "");
  if (words.length === 0) return "";

  const acronym = words
    .map((word) => word[0])
    .join("")
    .toUpperCase();
  if (acronym.length >= 2) return acronym.slice(0, 10);

  const first = words[0] ?? "";
  return first.slice(0, 4).toUpperCase();
}

interface FormState {
  name: string;
  key: string;
  description: string;
  ownerUserId: string;
}

function initialState(project?: Project): FormState {
  return {
    name: project?.name ?? "",
    key: project?.key ?? "",
    description: project?.description ?? "",
    ownerUserId: project?.ownerUserId ?? "",
  };
}

/**
 * One form for "New project" and "Edit project" (#399, bullets 1–2) — a
 * discriminated union rather than an optional `project` prop, the same
 * shape `ContactForm` uses so TypeScript, not a runtime check, stops a
 * create call from being built with a `project` nobody passed.
 *
 * Archiving lives in this same sheet rather than a separate action: there is
 * no per-project detail screen (`Projects.tsx`'s own comment on why), so
 * "edit" is the only place already holding one project's data to archive.
 */
type ProjectFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (project: Project) => void;
} & ({ mode: "create" } | { mode: "edit"; project: Project });

export function ProjectForm(props: ProjectFormProps) {
  const { open, onOpenChange, onSaved } = props;
  const data = useDataSource();
  const queryClient = useQueryClient();
  const usersQuery = useUserDirectory();

  const [state, setState] = useState<FormState>(() =>
    initialState(props.mode === "edit" ? props.project : undefined),
  );
  // Once someone edits the key by hand, typing in the name stops overwriting
  // it — otherwise a deliberate "OPS" would keep reverting to the suggestion
  // on the very next keystroke in the name field.
  const [keyTouched, setKeyTouched] = useState(false);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  function reset() {
    setState(initialState(props.mode === "edit" ? props.project : undefined));
    setKeyTouched(false);
    setRejection(null);
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      props.mode === "create"
        ? data.tasks.createProject({
            name: state.name.trim(),
            key: state.key.trim().toUpperCase(),
            description: state.description.trim() || null,
            ownerUserId: state.ownerUserId,
          })
        : data.tasks.updateProject(props.project.id, {
            name: state.name.trim(),
            description: state.description.trim() || null,
            ownerUserId: state.ownerUserId,
          }),
    onSuccess: (result) => {
      if (!result.ok) {
        setRejection(result.rejection);
        return;
      }
      onOpenChange(false);
      // Write-through: redraw from `result.data`, never from `state` —
      // CLAUDE.md's rule applies to a project the same as any other record.
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      onSaved?.(result.data);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => {
      if (props.mode !== "edit") throw new Error("nothing to archive yet");
      return data.tasks.archiveProject(props.project.id);
    },
    onSuccess: (result) => {
      if (!result.ok) {
        setRejection(result.rejection);
        return;
      }
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      onSaved?.(result.data);
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (props.mode === "create" && !KEY_PATTERN.test(state.key.toUpperCase())) {
      setRejection({
        kind: "invalid",
        message: "Check the highlighted fields.",
        fields: { key: "2–10 letters or numbers, starting with a letter" },
      });
      return;
    }
    setRejection(null);
    saveMutation.mutate();
  }

  const fieldError = (field: string): string | undefined =>
    rejection?.kind === "invalid" ? rejection.fields?.[field] : undefined;

  const pending = saveMutation.isPending || archiveMutation.isPending;
  const users = usersQuery.data?.items ?? [];

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <SheetContent
        title={props.mode === "create" ? "New project" : "Edit project"}
        description={
          props.mode === "create"
            ? "Tasks are filed under a project and referenced by its key, forever."
            : `Update ${props.project.name}'s details. The key cannot change.`
        }
        actions={<FeedbackButton />}
        className="w-full sm:w-[480px] sm:max-w-[92vw]"
      >
        <form className="flex h-full flex-col" onSubmit={handleSubmit}>
          <div className="flex-1 space-y-4 overflow-auto p-4 sm:p-6">
            <div className="space-y-1.5">
              <Label htmlFor="project-name">Name</Label>
              <Input
                id="project-name"
                value={state.name}
                onChange={(event) => {
                  const name = event.target.value;
                  setState((prev) => ({
                    ...prev,
                    name,
                    key:
                      props.mode === "create" && !keyTouched
                        ? suggestKey(name)
                        : prev.key,
                  }));
                }}
                required
              />
              {fieldError("name") ? (
                <p className="text-destructive text-xs">{fieldError("name")}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="project-key">Key</Label>
              <Input
                id="project-key"
                value={state.key}
                placeholder="e.g. OPS"
                maxLength={10}
                disabled={props.mode === "edit"}
                className="uppercase"
                onChange={(event) => {
                  setKeyTouched(true);
                  setState((prev) => ({
                    ...prev,
                    key: event.target.value.toUpperCase(),
                  }));
                }}
              />
              <p className="text-muted-foreground text-xs">
                {props.mode === "create"
                  ? "2–10 letters or numbers. Prefixes every task reference — OPS-142 — and cannot change once set."
                  : "Immutable: every task reference already says this project is this key."}
              </p>
              {fieldError("key") ? (
                <p className="text-destructive text-xs">{fieldError("key")}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                value={state.description}
                onChange={(event) => {
                  setState((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }));
                }}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="project-owner">Owner</Label>
              <Select
                value={state.ownerUserId}
                onValueChange={(value) => {
                  setState((prev) => ({ ...prev, ownerUserId: value }));
                }}
              >
                <SelectTrigger id="project-owner" className="w-full">
                  <SelectValue placeholder="Choose an owner" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldError("ownerUserId") ? (
                <p className="text-destructive text-xs">
                  {fieldError("ownerUserId")}
                </p>
              ) : null}
            </div>

            {rejection && rejection.kind !== "invalid" ? (
              <p className="text-destructive text-sm">
                {describeRejection(rejection)}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center justify-between gap-2 border-t p-4 sm:px-6">
            {props.mode === "edit" && !props.project.isArchived ? (
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={() => {
                  setRejection(null);
                  archiveMutation.mutate();
                }}
              >
                {archiveMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : (
                  <Archive />
                )}
                Archive project
              </Button>
            ) : (
              <span />
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  reset();
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  pending ||
                  state.name.trim() === "" ||
                  state.ownerUserId === "" ||
                  (props.mode === "create" && state.key.trim() === "")
                }
              >
                {saveMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                ) : null}
                {props.mode === "create" ? "Create project" : "Save changes"}
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
